import { useMemo, useState } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { faTrashCan } from "@fortawesome/pro-solid-svg-icons";
import {
  AdminFormPage,
  AdminListCard,
  AdminListTable,
  ConfirmDeleteModal,
  saveErrorReason,
  saveErrorToast,
  type BatchAction,
  useBatchSelection,
  withFailureReasons,
} from "@components/CatalogIndex";
import type { BatchFailure } from "@components/CatalogIndex";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import {
  useV2ListResourcesForAccountQuery,
  useV2ListServiceCategorysForAccountQuery,
} from "@generated";
import { useResourceRatesList, useResourceRateMutations } from "../api";
import { ResourceRateImportModal } from "../ResourceRateImportModal/ResourceRateImportModal";
import { ResourceRateForm } from "./ResourceRateForm";
import { emptyResourceRateForm } from "../types";
import type { ResourceRate, ResourceRateFormValues } from "../types";

const FORM_ID = "resource-rate-form";
const PAGE_SIZE = 25;

type View =
  | { mode: "list" }
  | { mode: "new" }
  | { mode: "edit"; row: ResourceRate };

export interface ResourceRatesTabProps {
  rateTableId: string;
}

export const ResourceRatesTab = ({
  rateTableId,
}: ResourceRatesTabProps): JSX.Element => {
  const [view, setView] = useState<View>({ mode: "list" });
  const [pageNumber, setPageNumber] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [toast, setToast] = useState<{
    variant: "success" | "danger";
    text: string;
  } | null>(null);

  const sel = useBatchSelection([pageNumber]);

  const { rows, pageMeta, isLoading, isFetching, refetch } =
    useResourceRatesList({
      rateTableId,
      page: { number: pageNumber, size: PAGE_SIZE },
    });
  const { create, update, destroy } = useResourceRateMutations(rateTableId);

  const selectedRates = useMemo(
    () => rows.filter((r) => sel.selectedIds.includes(r.id)),
    [rows, sel.selectedIds]
  );

  const { data: resourceData } = useV2ListResourcesForAccountQuery({
    filter: { active: true },
  } as any);
  // The LOB select is populated from service categories (child sections), shown
  // by nested-name and sorted alphabetically. The resource-rate `line-of-business`
  // relationship just writes `section_id`, and categories are sections, so the
  // selected category's id is a valid section_id (the linkage type stays
  // `line-of-businesses`, which is what the relationship expects).
  const { data: categoryData } = useV2ListServiceCategorysForAccountQuery({
    filter: { active: true },
    sort: ["nested-name"],
  } as any);

  const resourceOptions = useMemo(
    () =>
      (resourceData?.data ?? []).map((r: any) => ({
        value: String(r.id),
        label: r.attributes?.name ?? "",
      })),
    [resourceData]
  );
  // Prepend a blank option so a previously-set Line of Business can be cleared
  // back to "all" (RequiredReactSelect has no clear button). Categories are
  // shown by nested-name, sorted alphabetically.
  const lobOptions = useMemo(
    () => [
      { value: "", label: "All Lines of Business" },
      ...(categoryData?.data ?? [])
        .map((c: any) => ({
          value: String(c.id),
          label: c.attributes?.["nested-name"] ?? "",
        }))
        .sort((a: { label: string }, b: { label: string }) =>
          a.label.localeCompare(b.label)
        ),
    ],
    [categoryData]
  );

  const columns: ColumnDef<ResourceRate>[] = [
    {
      accessorKey: "resourceName",
      header: "Resource",
      cell: ({ row }) => row.original.resourceName,
    },
    {
      accessorKey: "externalName",
      header: "External Name",
      cell: ({ row }) => row.original.externalName,
    },
    {
      accessorKey: "description",
      header: "Description",
      cell: ({ row }) => row.original.description,
    },
    {
      accessorKey: "lobName",
      header: "LOB / Category",
      cell: ({ row }) => row.original.lobName || "—",
    },
    {
      accessorKey: "hourlyRate",
      header: "Price",
      cell: ({ row }) => row.original.hourlyRate ?? "",
    },
    {
      accessorKey: "hourlyCost",
      header: "Cost",
      cell: ({ row }) => row.original.hourlyCost ?? "",
    },
  ];

  const batchActions: BatchAction[] = [
    {
      icon: faTrashCan,
      label: "Delete",
      onClick: () => setConfirmOpen(true),
      variant: "danger",
    },
  ];

  const handleSubmit = async (values: ResourceRateFormValues) => {
    setSubmitting(true);
    try {
      if (view.mode === "edit") {
        await update(view.row.id, values);
      } else {
        await create(values);
      }
      setView({ mode: "list" });
      setToast({ variant: "success", text: "Resource rate saved." });
      refetch();
    } catch (err) {
      console.error("Failed to save resource rate", err);
      setToast(saveErrorToast(err, "resource rate"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmDelete = async () => {
    setConfirmOpen(false);
    const targets = [...selectedRates];
    const failures: BatchFailure[] = [];
    for (const r of targets) {
      try {
        await destroy(r.id);
      } catch (err) {
        failures.push({ label: r.resourceName, reason: saveErrorReason(err) });
      }
    }
    setToast(
      failures.length === 0
        ? {
            variant: "success",
            text: `${targets.length} resource rate(s) deleted.`,
          }
        : {
            variant: "danger",
            text: withFailureReasons(
              `Failed on ${failures.length} of ${targets.length} resource rate(s).`,
              failures
            ),
          }
    );
    sel.clear();
    refetch();
  };

  if (isLoading) return <ScopeStackSpinner />;

  if (view.mode !== "list") {
    const initialValues: ResourceRateFormValues =
      view.mode === "edit"
        ? {
            resourceId: view.row.resourceId,
            externalName: view.row.externalName,
            description: view.row.description,
            lobId: view.row.lobId ?? "",
            hourlyRate: view.row.hourlyRate,
            hourlyCost: view.row.hourlyCost,
            expenseRate: view.row.expenseRate,
            code: view.row.code,
          }
        : emptyResourceRateForm;

    return (
      <>
        <AdminFormPage
          title={
            view.mode === "edit" ? "Edit Resource Rate" : "Add Resource Rate"
          }
          formId={FORM_ID}
          onCancel={() => setView({ mode: "list" })}
          isSubmitting={submitting}
        >
          <ResourceRateForm
            id={FORM_ID}
            initialValues={initialValues}
            resourceOptions={resourceOptions}
            lobOptions={lobOptions}
            isEditing={view.mode === "edit"}
            isSubmitting={submitting}
            onSubmit={handleSubmit}
          />
        </AdminFormPage>
        {toast && (
          <ToastAlert
            variant={toast.variant}
            text={toast.text}
            onClose={() => setToast(null)}
          />
        )}
      </>
    );
  }

  return (
    <>
      <AdminListCard
        title="Resource Rates"
        onNew={() => setView({ mode: "new" })}
        newButtonLabel="+ Add Resource Rate"
        secondaryAction={{
          label: "Import Rates",
          onClick: () => setImportOpen(true),
        }}
        batchActions={batchActions}
        selectedCount={sel.selectedIds.length}
      >
        <AdminListTable
          columns={columns}
          data={rows}
          isLoading={isFetching}
          selectedIds={sel.selectedIds}
          setSelectedIds={sel.setSelectedIds}
          onEdit={(id) => {
            const row = rows.find((r) => r.id === String(id));
            if (row) setView({ mode: "edit", row });
          }}
          emptyMessage="No resource rates found."
          paginationEnabled
          totalPages={pageMeta.pageCount}
          totalRows={pageMeta.recordCount}
          currentPage={pageNumber}
          setCurrentPage={(p) => {
            sel.clear();
            setPageNumber(p);
          }}
        />
      </AdminListCard>

      <ResourceRateImportModal
        show={importOpen}
        rateTableId={rateTableId}
        onClose={() => setImportOpen(false)}
        onError={(text) => {
          setImportOpen(false);
          setToast({ variant: "danger", text });
        }}
      />

      <ConfirmDeleteModal
        open={confirmOpen}
        title="Delete resource rates?"
        message={`Are you sure you want to delete ${sel.selectedIds.length} resource rate(s)?`}
        confirmLabel="Delete"
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmOpen(false)}
      />

      {toast && (
        <ToastAlert
          variant={toast.variant}
          text={toast.text}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};

export default ResourceRatesTab;
